import localforage from 'localforage';

// Application constants
const APPLICATION_NAME = 'UMS-surveys';
const OFFLINE_STORE_NAME = 'offline_surveys_store';
const OFFLINE_STORE_KEY = 'offline_surveys';

// Define a type for what is stored — you can replace `any` with your real survey type
type OfflineEntry = any;
type OfflineStore = Record<string, OfflineEntry>;

// Configure localforage
localforage.config({
    name: APPLICATION_NAME,
    storeName: OFFLINE_STORE_NAME,
    description: 'Durable storage for offline surveys',
});

/**
 * Notify the UI when the offline store changes.
 */
function notifyStoreChanged(): void {
    try {
        window.dispatchEvent(new CustomEvent('offlineSurveys:changed'));
    } catch {
        // Ignore if notifications cannot be sent
    }
}

/**
 * Deep clone data to ensure it is JSON serializable.
 */
function toSerializable<T = any>(data: T): T {
    try {
        return JSON.parse(JSON.stringify(data));
    } catch {
        return {} as T;
    }
}

/**
 * Retrieve all entries stored in IndexedDB.
 */
export async function getAllEntries(): Promise<OfflineStore> {
    const data = await localforage.getItem<OfflineStore>(OFFLINE_STORE_KEY);
    return ((data !== undefined && data !== null) ? data : {}) as OfflineStore;
}


/**
 * Overwrite all entries in the offline store.
 */
export async function setAllEntries(storeData: OfflineStore): Promise<void> {
    const serialized = toSerializable(storeData);
    await localforage.setItem(OFFLINE_STORE_KEY, serialized);
    notifyStoreChanged();
}

/**
 * Retrieve a single entry by its ID.
 */
export async function getEntry(id: string): Promise<OfflineEntry | null> {
    const allEntries = await getAllEntries();
    return Object.prototype.hasOwnProperty.call(allEntries, id)
        ? allEntries[id]
        : null;
}

/**
 * Add or update a single entry in the offline store.
 */
export async function upsertEntry(id: string, partialData: any): Promise<void> {
    const allEntries = await getAllEntries();
    const existing = allEntries[id] || {};
    const merged = { ...existing, ...partialData };
    allEntries[id] = toSerializable(merged);
    await setAllEntries(allEntries); // triggers UI update
    // Optional debug:
    // console.debug('Saved entry:', await getEntry(id));
}

/**
 * Remove a single entry by its ID.
 */
export async function removeEntry(id: string): Promise<void> {
    const allEntries = await getAllEntries();
    delete allEntries[id];
    await setAllEntries(allEntries); // triggers UI update
}

/**
 * Clear all stored survey entries.
 */
export async function clearAllEntries(): Promise<void> {
    await setAllEntries({}); // triggers UI update
}

/**
 * Migrate data from legacy localStorage into IndexedDB (if any).
 */
export async function migrateFromLocalStorageIfPresent(): Promise<void> {
    const legacyData = localStorage.getItem('offline_surveys');
    if (legacyData) {
        try {
            const parsed = JSON.parse(legacyData);
            await localforage.setItem(OFFLINE_STORE_KEY, parsed);
            localStorage.removeItem('offline_surveys');
            notifyStoreChanged();
            console.log('Migrated offline_surveys to IndexedDB');
        } catch (e) {
            console.warn('Migration failed:', e);
        }
    }
}

/**
 * Request persistent storage so that offline data is not automatically cleared by the browser.
 */
export async function requestPersistentStorage(): Promise<boolean> {
    if (navigator.storage && typeof navigator.storage.persist === 'function') {
        const persisted = await navigator.storage.persist();
        console.log('Storage persistence:', persisted);
        return persisted;
    }
    return false;
}

/**
 * Backward-compatible aliases (you can remove these if you update all imports).
 */
export const getAll = getAllEntries;
export const setAll = setAllEntries;
export const get = getEntry;
export const upsert = upsertEntry;
export const remove = removeEntry;
export const clear = clearAllEntries;
export const requestDurablePersistence = requestPersistentStorage;
